// The questionnaire as a client would see it, rendered from the editor's
// draft. The answers, the case type and the files offered live in this
// window only; nothing is saved or sent. The state sits inside the Dialog,
// which mounts nothing while closed, so every open starts fresh and no
// words are asked for until then.

import { useRef, useState } from "react";
import type {
  IntakeAnswerValue,
  IntakeLanguage,
  IntakeTemplate,
  IntakeUploadedDocument,
} from "@duralex/shared";
import { Dialog } from "../../../../components/chrome/Dialog";
import { Skeleton, SkeletonGroup } from "../../../../components/chrome/Skeleton";
import { QuestionnaireForm } from "../QuestionnaireForm";
import { useIntakeWords } from "../../hooks/useIntakeWords";
import { outstanding } from "../../lib/intakeLogic";

interface Props {
  open: boolean;
  onClose: () => void;
  template: IntakeTemplate;
  /** English, then every language the draft speaks whole. */
  languages: IntakeLanguage[];
}

export function PreviewDialog({ open, onClose, template, languages }: Props) {
  return (
    <Dialog open={open} onClose={onClose} title="Preview" className="ie-preview">
      <Preview template={template} languages={languages} />
    </Dialog>
  );
}

function Preview({ template, languages }: Pick<Props, "template" | "languages">) {
  const [locale, setLocale] = useState<IntakeLanguage>("en");
  const [category, setCategory] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, IntakeAnswerValue>>({});
  const [documents, setDocuments] = useState<IntakeUploadedDocument[]>([]);
  const [sent, setSent] = useState(false);
  const fileSeq = useRef(0);
  const { words, t, spokenIn, isError, refetch } = useIntakeWords(locale);

  if (isError && !words) {
    return (
      <div className="iform-note-card" lang={spokenIn}>
        <h1>{t("error_title")}</h1>
        <p>{t("error_text")}</p>
        <button type="button" className="cc-btn-primary" onClick={() => void refetch()}>
          {t("error_retry")}
        </button>
      </div>
    );
  }
  if (!words) {
    return (
      <SkeletonGroup label="Loading the questionnaire">
        <Skeleton width="45%" />
        <Skeleton width="80%" />
        <Skeleton width="70%" />
      </SkeletonGroup>
    );
  }
  // The card stays mounted under the done card: Back returns to the same
  // typed drafts and ticked agreements.
  return (
    <>
      <p className="ie-hint" role="note">
        A preview of this draft: answers stay in this window, files are not uploaded, nothing is
        sent.
      </p>
      {sent && (
        <>
          <div className="iform-note-card" lang={spokenIn}>
            <h1>{t("done_title")}</h1>
            <p>{t("done_text")}</p>
          </div>
          <div className="cc-form-actions ie-preview-back">
            <button type="button" className="cc-link-btn" autoFocus onClick={() => setSent(false)}>
              Back to the form
            </button>
          </div>
        </>
      )}
      <div hidden={sent}>
        <QuestionnaireForm
          template={template}
          locale={locale}
          languages={languages}
          t={t}
          category={category}
          categoryLocked={false}
          answers={answers}
          ticks={{}}
          documents={documents}
          outstanding={outstanding(
            template,
            category,
            answers,
            documents.map((d) => d.item),
          )}
          notes={{}}
          saving={false}
          uploading={false}
          removing={false}
          submitting={false}
          alerts={{}}
          onLanguage={setLocale}
          onCategory={setCategory}
          onAnswer={(id, value) => setAnswers((a) => ({ ...a, [id]: value }))}
          onUpload={(item, file) =>
            setDocuments((d) => [
              ...d,
              {
                id: `preview-${(fileSeq.current += 1)}`,
                item,
                fileName: file.name,
                size: file.size,
                uploadedAt: new Date().toISOString(),
              },
            ])
          }
          onRemoveDoc={(id) => setDocuments((d) => d.filter((x) => x.id !== id))}
          onSubmit={() => setSent(true)}
        />
      </div>
    </>
  );
}
